import React, { useState, useCallback } from 'react';
import { useAllUsersQuery } from '../hooks/requests/queries/use_all_users_query';
import type { PartnerUser } from '../hooks/requests/queries/use_all_users_query';
import { ErrorFallback, EmptyState } from '../components/feedback';
import {
  VoiceDemoSkeleton,
  UserSelector,
  VoicePanel,
  CreateUserModal,
  VoiceStatsGrid,
} from '../components/voice-demo';
import { useConversationsQuery } from '../hooks/voice-demo/useConversationsQuery';

const VoiceDemoPage: React.FC = () => {
  const { data: users, isLoading: isUsersLoading, error: usersError, refetch } = useAllUsersQuery();

  const [selectedUser, setSelectedUser] = useState<PartnerUser | null>(null);
  const [isCreateUserOpen, setIsCreateUserOpen] = useState(false);
  // Incremented to force VoicePanel remount (user change or conversation deleted)
  const [sessionKey, setSessionKey] = useState(0);

  const handleSelectUser = useCallback((user: PartnerUser) => {
    setSelectedUser(user);
    setSessionKey((k) => k + 1);
  }, []);

  const handleConversationDeleted = useCallback(() => {
    setSessionKey((k) => k + 1); // force VoicePanel remount → clears messages + WS state
  }, []);

  // Must be called before any early returns (Rules of Hooks)
  const { data: currentConversation } = useConversationsQuery(
    selectedUser?.user_id ?? null,
    selectedUser?.company_id ?? null
  );

  // Loading state
  if (isUsersLoading) {
    return <VoiceDemoSkeleton />;
  }

  // Error state
  if (usersError) {
    return (
      <div className="voice-demo-page">
        <div className="api-keys-header">
          <h1 className="api-keys-title">Ally Voice</h1>
          <p className="api-keys-subtitle">Test the voice experience with your users</p>
        </div>

        <div className="au-card">
          <ErrorFallback
            error={usersError}
            title="Failed to load users"
            onRetry={() => void refetch()}
          />
        </div>
      </div>
    );
  }

  const userList = users || [];
  const messageCount =
    currentConversation?.messageCount || currentConversation?.messages?.length || 0;

  return (
    <div className="voice-demo-page">
      <div className="api-keys-header">
        <h1 className="api-keys-title">Ally Voice</h1>
        <p className="api-keys-subtitle">Test the voice experience with your users</p>
      </div>

      {userList.length === 0 ? (
        /* Empty state - no users */
        <div className="au-card">
          <EmptyState
            title="No users yet"
            description="Create a user to get started with the voice demo."
            action={{
              label: '+ New user',
              onClick: () => setIsCreateUserOpen(true),
            }}
          />
        </div>
      ) : (
        <div className="space-y-6">
          {/* Quick Stats */}
          <VoiceStatsGrid
            totalUsers={userList.length}
            selectedUser={selectedUser}
            messageCount={messageCount}
          />

          {/* User selector card */}
          <UserSelector
            users={userList}
            selectedUser={selectedUser}
            onSelectUser={handleSelectUser}
            onCreateUser={() => setIsCreateUserOpen(true)}
          />

          {/* Main content */}
          {!selectedUser ? (
            <div className="au-card">
              <EmptyState
                title="Select a user"
                description="Choose a user from the dropdown above to start a voice session."
                icon={
                  <svg
                    className="w-6 h-6"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={1.5}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z"
                    />
                  </svg>
                }
              />
            </div>
          ) : (
            <VoicePanel
              key={`${selectedUser.user_id}-${sessionKey}`}
              selectedUser={selectedUser}
              conversationId={null}
              onDeleted={handleConversationDeleted}
            />
          )}
        </div>
      )}

      {/* Create user modal */}
      <CreateUserModal
        isOpen={isCreateUserOpen}
        onClose={() => setIsCreateUserOpen(false)}
        users={userList}
      />
    </div>
  );
};

export default VoiceDemoPage;
